import React from 'react';
import { Sparkles, TrendingUp, Users, BookOpen, Award, Briefcase, ArrowRight } from 'lucide-react';

/**
 * Preset Explorer - Browse and load example life scenarios
 */
const PresetExplorer = ({ onLoadPreset, darkMode, onClose }) => {
  // Helper function to format decimal age to years/months/days
  const formatRetirementAge = (decimalAge) => {
    if (!decimalAge || isNaN(decimalAge)) return 'Not set';
    const years = Math.floor(decimalAge);
    const fractionalYear = decimalAge - years;
    const months = Math.floor(fractionalYear * 12);
    const days = Math.floor((fractionalYear * 12 - months) * 30);
    return `${years}y ${months}m ${days}d`;
  };

  const presets = [
    {
      id: 'fire-45',
      name: 'Software Engineer — FIRE at 45',
      icon: TrendingUp,
      description: 'High earner pursuing early retirement through aggressive saving',
      age: 30,
      salary: '$140k',
      retireAge: 45,
      highlights: ['Owns home', 'High savings rate', 'Aggressive portfolio']
    },
    {
      id: 'coast-fire',
      name: 'Coast FIRE at 35',
      icon: Sparkles,
      description: 'Build nest egg early, then coast to traditional retirement',
      age: 25,
      salary: '$95k',
      retireAge: 60,
      highlights: ['Renting', 'Front-loaded savings', 'Balanced lifestyle']
    },
    {
      id: 'dink',
      name: 'DINK Couple',
      icon: Users,
      description: 'Dual income, no kids, retiring early together',
      age: 32,
      salary: '$180k',
      retireAge: 50,
      highlights: ['Combined income', 'Homeowners', 'Travel-focused']
    },
    {
      id: 'teacher',
      name: 'Teacher Salary',
      icon: BookOpen,
      description: 'Steady income with pension, traditional retirement path',
      age: 28,
      salary: '$55k',
      retireAge: 65,
      highlights: ['Steady career', 'Pension income', 'Modest lifestyle']
    },
    {
      id: 'military',
      name: 'Military Career (20 years)',
      icon: Award,
      description: 'Serve 20 years, retire at 42 with pension and benefits',
      age: 22,
      salary: '$48k',
      retireAge: 42,
      highlights: ['Housing included', 'Pension at 42', 'Second career option']
    },
    {
      id: 'mid-career',
      name: 'Mid-Career Professional',
      icon: Briefcase,
      description: 'Age 40 with established career, aiming for early retirement',
      age: 40,
      salary: '$110k',
      retireAge: 60,
      highlights: ['Homeowner', 'Catching up', 'Strong income']
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className={`rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto ${
        darkMode ? 'bg-slate-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`sticky top-0 border-b-2 p-6 ${
          darkMode ? 'bg-slate-900 border-slate-700' : 'bg-gradient-to-r from-teal-50 to-blue-50 border-teal-200'
        }`}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Explore Life Presets
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Load a preset scenario to explore different life paths and financial decisions
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                darkMode ? 'hover:bg-slate-800 text-gray-400' : 'hover:bg-white text-gray-600'
              }`}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Preset Grid */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {presets.map((preset) => {
            const Icon = preset.icon;
            return (
              <button
                key={preset.id}
                onClick={() => onLoadPreset(preset.id)}
                className={`text-left p-5 rounded-xl border-2 transition-all hover:scale-[1.02] hover:shadow-lg ${
                  darkMode
                    ? 'bg-slate-800 border-slate-700 hover:border-teal-500'
                    : 'bg-white border-gray-200 hover:border-teal-400'
                }`}
              >
                {/* Icon and Title */}
                <div className="flex items-start gap-3 mb-3">
                  <div className={`p-2 rounded-lg ${
                    darkMode ? 'bg-teal-900/40' : 'bg-teal-100'
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      darkMode ? 'text-teal-400' : 'text-teal-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-bold text-base mb-1 ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {preset.name}
                    </h3>
                    <p className={`text-xs ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {preset.description}
                    </p>
                  </div>
                </div>

                {/* Stats */}
                <div className={`grid grid-cols-3 gap-2 mb-3 p-2 rounded-lg ${
                  darkMode ? 'bg-slate-900/50' : 'bg-gray-50'
                }`}>
                  <div>
                    <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Age</div>
                    <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {preset.age}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Income</div>
                    <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {preset.salary}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Retire</div>
                    <div className={`text-sm font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                      {formatRetirementAge(preset.retireAge)}
                    </div>
                  </div>
                </div>

                {/* Highlights */}
                <div className="space-y-1">
                  {preset.highlights.map((highlight, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <ArrowRight className={`w-3 h-3 ${
                        darkMode ? 'text-teal-500' : 'text-teal-600'
                      }`} />
                      <span className={`text-xs ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {highlight}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Load button overlay */}
                <div className={`mt-3 pt-3 border-t ${
                  darkMode ? 'border-slate-700' : 'border-gray-200'
                }`}>
                  <div className={`text-sm font-medium text-center ${
                    darkMode ? 'text-teal-400' : 'text-teal-600'
                  }`}>
                    Load Preset →
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className={`border-t-2 p-4 text-center text-xs ${
          darkMode ? 'bg-slate-800 border-slate-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'
        }`}>
          💡 Tip: After loading a preset, use the AI to modify it or compare different lifestyle choices
        </div>
      </div>
    </div>
  );
};

export default PresetExplorer;
